// Qual consumo o Rateio usa para uma UC — ponto ÚNICO de leitura.
//
// Antes, cada lugar do módulo lia `uc.consumo_medio_kwh || 0` direto. Trocar a
// fonte em sete lugares diferentes é como se cria divergência entre a tela e o
// servidor. Aqui a decisão acontece uma vez e todo mundo chama a mesma função.
//
// A regra de escolha é a mesma que a RPC `travar_uc_excesso_saldo` aplica no
// banco (migration 20260828100000): observado só com ≥3 faturas e recalculado há
// ≤45 dias; senão, a estimativa da análise cadastral, declarada como tal.
//
//   node --test src/lib/__tests__/consumoDaUc.test.ts

import {
  resolverConsumoOperacional,
  type ConsumoOperacional,
} from './consumoOperacional.ts';
import { DISPERSAO_INACEITAVEL } from './consumoObservado.ts';

/** O mínimo que uma linha de UC precisa ter para o consumo ser resolvido. */
export interface UcComConsumo {
  consumo_medio_kwh?: number | null;
  consumo_observado_kwh?: number | null;
  consumo_observado_meses?: number | null;
  consumo_observado_em?: string | null;
  /** Coeficiente de variação dos meses observados — o detector de fatura corrompida. */
  consumo_observado_dispersao?: number | null;
}

/**
 * Resolve o consumo operacional da UC, com a fonte e o aviso para a tela.
 * A confiança sai do número de faturas — a dispersão não é persistida, então o
 * pior caso aqui é uma confiança otimista, nunca um número inventado.
 */
export function consumoOperacionalDaUc(uc: UcComConsumo | null | undefined, hoje?: Date): ConsumoOperacional {
  const meses = Number(uc?.consumo_observado_meses) || 0;
  const dispersao = Number(uc?.consumo_observado_dispersao) || 0;

  // Meses que discordam demais entre si sinalizam fatura com leitura incorreta.
  // Levantamento em produção (28/08/2026): 10 UCs em 525 passam de 0,6 — uma
  // delas (438, residencial B1) tinha média de 45.020 kWh vinda de dois meses de
  // 67.072 idênticos ao lado de um mês de 916. Sazonalidade real de Goiás fica em
  // 0,15–0,25, e a UC 379 (comercial, 10 a 14 MWh consistentes) NÃO cai aqui —
  // que é a distinção que importa: alto de verdade passa, errático é marcado.
  //
  // MARCA, NÃO DESCARTA. Das 10, quatro são UCs da própria Vigor (Associação e
  // Gestão), que têm papel próprio no rateio e cadastro zerado. Trocar o número
  // delas pela estimativa faria a tela mostrar zero justamente onde o analista
  // precisa enxergar. Quem lê a marca e recusa agir é o travamento automático.
  const suspeito = dispersao > DISPERSAO_INACEITAVEL;

  const resolvido = resolverConsumoOperacional({
    observadoKwh: uc?.consumo_observado_kwh ?? null,
    mesesObservados: meses,
    confianca: meses >= 12 ? 'alta' : meses >= 5 ? 'media' : meses > 0 ? 'baixa' : 'sem_dados',
    estimativaCadastralKwh: uc?.consumo_medio_kwh ?? null,
    observadoEm: uc?.consumo_observado_em ?? null,
    hoje,
  });

  if (!suspeito) return resolvido;

  return {
    ...resolvido,
    suspeito: true,
    aviso:
      `Consumo instável: os meses variam ${Math.round(dispersao * 100)}% entre si, ` +
      'sinal de fatura com leitura incorreta. O número segue disponível, mas confira ' +
      'o histórico antes de usá-lo — o travamento automático não age sobre esta UC.',
  };
}

/**
 * Atalho para os pontos do balanceamento que só precisam do número.
 *
 * Devolve 0 quando não há base alguma — que é exatamente o que
 * `uc.consumo_medio_kwh || 0` já fazia nesses lugares. Quem precisa distinguir
 * "zero" de "não sei" usa `consumoOperacionalDaUc` e olha a `fonte`.
 */
export function consumoParaAlocacao(uc: UcComConsumo | null | undefined, hoje?: Date): number {
  return consumoOperacionalDaUc(uc, hoje).valorKwh ?? 0;
}
